/**
 * Per quanto resta pubblicato un documento: la regola dell'obbligo, N anni, o
 * fino a una data.
 *
 * Il valore di partenza e' la regola dell'obbligo, scritta per esteso — «Come
 * da regola dell'obbligo: 3 anni» — e non un campo vuoto: chi carica deve
 * vedere che cosa succedera' se non tocca nulla, che e' il caso normale. Le
 * eccezioni sono due: una durata propria in anni (un regolamento in vigore per
 * dieci) e una data di fine (l'incarico che si pubblica per tre anni dalla
 * cessazione, l'atto che resta finche' produce effetti, art. 8 c. 3).
 *
 * La logica — che cosa vale ogni scelta, quando e' sbagliata — sta in
 * `lib/scadenze.ts` (`leggiDurata`), dove le verifiche la raggiungono; qui c'e'
 * solo il modulo.
 */

import {
  anniDelMenu,
  anniInTesto,
  DURATA_MASSIMA_ANNI,
  DURATA_PREDEFINITA_ANNI,
  etichettaDurataPredefinita,
  leggiDurata,
} from "../lib/scadenze";
import type { SceltaDurata } from "../lib/scadenze";
import { CampoDato, CampoScelta } from "./campi";
import type { OpzioneScelta } from "./campi";

/** Le voci del menu, con la regola dell'obbligo in testa. */
function opzioni(
  durataObbligo: number | null | undefined,
  regoleNote: boolean,
  massimoAnni: number,
): OpzioneScelta[] {
  const regola = etichettaDurataPredefinita(durataObbligo, regoleNote);
  const anni: OpzioneScelta[] = anniDelMenu(massimoAnni).map((valore) => ({
    valore: `anni:${valore}`,
    etichetta: `Per ${anniInTesto(valore)}`,
  }));
  return [{ valore: "", etichetta: regola }, ...anni, { valore: "data", etichetta: "Fino a una data…" }];
}

export function CampoDurata({
  scelta,
  finoAl,
  onCambia,
  durataObbligo,
  regoleNote = true,
  etichetta = "Durata di pubblicazione",
  massimoAnni = DURATA_MASSIMA_ANNI,
}: {
  scelta: SceltaDurata;
  finoAl: string;
  onCambia: (scelta: SceltaDurata, finoAl: string) => void;
  /** Durata dichiarata nelle regole dell'obbligo, se c'e'. */
  durataObbligo?: number | null;
  /**
   * Falso finche' le regole degli obblighi non sono arrivate, o se non sono
   * arrivate: la prima voce non nomina allora alcuna durata.
   */
  regoleNote?: boolean;
  etichetta?: string;
  /** Ultima durata in anni offerta dal menu; oltre resta «Fino a una data…». */
  massimoAnni?: number;
}) {
  const letta = leggiDurata(scelta, finoAl);
  const anni = letta.durata_anni;
  // Sotto i cinque anni si va solo dove una norma lo prevede: lo si dice senza
  // impedirlo, perche' quei casi esistono (artt. 14 e 15).
  const avvisoBreve =
    anni !== null && anni < DURATA_PREDEFINITA_ANNI
      ? "Meno dei cinque anni dell'art. 8 c. 3: solo dove una norma lo prevede, per esempio i dati degli artt. 14 e 15."
      : undefined;
  return (
    <div className="pila-stretta">
      <CampoScelta
        etichetta={etichetta}
        valore={scelta}
        onCambia={(valore) => onCambia(valore as SceltaDurata, finoAl)}
        opzioni={opzioni(durataObbligo, regoleNote, massimoAnni)}
        vuoto={null}
        aiuto={
          avvisoBreve ??
          "Trascorsa la durata, il documento esce dalla pubblicazione corrente e passa nell'archivio della pagina: resta accessibile, non si cancella."
        }
      />
      {scelta === "data" ? (
        <CampoDato
          etichetta="Ultimo giorno di pubblicazione"
          tipo="data"
          valore={finoAl}
          onCambia={(valore) => onCambia(scelta, valore)}
          obbligatorio
          errore={finoAl.trim() ? letta.errore : null}
          aiuto="Compreso: il documento esce il giorno dopo."
        />
      ) : null}
    </div>
  );
}
